import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { getImageUrl } from '../utils/imageUrl';
import { log } from '../utils/logger';
import './AdminNews.css';

const AdminNews = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const quillRef = useRef(null);
  const quillEditorRef = useRef(null);
  const [activeTab, setActiveTab] = useState('news'); // 'news', 'banners', 'guides', 'topImages' ou 'youtubeVideos'
  const [news, setNews] = useState([]);
  const [banners, setBanners] = useState([]);
  const [guides, setGuides] = useState([]);
  const [topImages, setTopImages] = useState([]);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);
  const [editingGuide, setEditingGuide] = useState(null);
  const [editingTopImage, setEditingTopImage] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    published: false
  });
  const [bannerFormData, setBannerFormData] = useState({
    title: '',
    link_url: '',
    active: true,
    display_order: 0
  });
  const [topImageFormData, setTopImageFormData] = useState({
    title: '',
    link_url: '',
    active: true,
    display_order: 0
  });
  const [videoFormData, setVideoFormData] = useState({
    youtube_url: '',
    title: '',
    description: '',
    active: true,
    display_order: 0
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [message, setMessage] = useState('');

  // Interceptar erros globais do Quill
  useEffect(() => {
    const handleError = (event) => {
      if (event.error && event.error.message && event.error.message.includes('addRange')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };
    
    window.addEventListener('error', handleError);
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);

  useEffect(() => {
    if (authLoading) return; // Aguardar autenticação terminar
    
    if (!user || !user.isAdmin) {
      navigate('/');
      return;
    }
    fetchNews();
    fetchBanners();
    fetchGuides();
    fetchTopImages();
    fetchYoutubeVideos();
  }, [user, navigate, authLoading]);

  const fetchNews = async () => {
    try {
      const response = await api.get('/news/admin/all');
      setNews(response.data.news || []);
    } catch (error) {
      log.error('Erro ao buscar notícias:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBanners = async () => {
    try {
      const response = await api.get('/news/banners/admin/all');
      setBanners(response.data.banners || []);
    } catch (error) {
      log.error('Erro ao buscar banners:', error);
    }
  };

  const fetchGuides = async () => {
    try {
      const response = await api.get('/guides/admin/all');
      setGuides(response.data.guides || []);
    } catch (error) {
      log.error('Erro ao buscar guias:', error);
    }
  };

  const fetchTopImages = async () => {
    try {
      const response = await api.get('/news/top-images/admin/all');
      setTopImages(response.data.topImages || []);
    } catch (error) {
      log.error('Erro ao buscar imagens de topo:', error);
    }
  };

  const fetchYoutubeVideos = async () => {
    try {
      const response = await api.get('/news/youtube-videos/admin/all');
      setYoutubeVideos(response.data.videos || []);
    } catch (error) {
      log.error('Erro ao buscar vídeos do YouTube:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentChange = useCallback((content) => {
    // Usar requestAnimationFrame para garantir que o DOM está atualizado
    requestAnimationFrame(() => {
      try {
        setFormData(prev => ({ ...prev, content }));
      } catch (error) {
        // Ignorar erros do Quill relacionados a ranges inválidos
        if (error.message && error.message.includes('addRange')) {
          return;
        }
        log.warn('Erro ao atualizar conteúdo:', error);
      }
    });
  }, []);

  // Função para fazer upload de imagem inline
  const uploadImage = useCallback(async (file) => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      // Determinar qual endpoint usar baseado na aba ativa
      const endpoint = activeTab === 'guides' ? '/guides/upload-image' : '/news/upload-image';
      const response = await api.post(endpoint, formData);

      // A URL retornada já vem como /uploads/filename
      return response.data.url; // Já vem como /uploads/filename
    } catch (error) {
      log.error('Erro ao fazer upload de imagem:', error);
      throw error;
    }
  }, [activeTab]);

  // Configuração do ReactQuill com handler de imagens
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: () => {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'image/*');
          input.click();

          input.onchange = async () => {
            const file = input.files[0];
            if (file) {
              try {
                const url = await uploadImage(file);
                const quill = quillEditorRef.current;
                if (quill) {
                  try {
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range ? range.index : 0, 'image', getImageUrl(url));
                    quill.setSelection((range ? range.index : 0) + 1);
                  } catch (rangeError) {
                    // Se houver erro de range, inserir no final
                    const length = quill.getLength();
                    quill.insertEmbed(length - 1, 'image', getImageUrl(url));
                  }
                }
              } catch (error) {
                alert('Erro ao fazer upload da imagem. Tente novamente.');
              }
            }
          };
        }
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), [uploadImage]);

  // Handler para colar imagens (Ctrl+V)
  const handleImagePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) {
          uploadImage(file).then(url => {
            const quill = quillEditorRef.current;
            if (quill) {
              const range = quill.getSelection(true);
              quill.insertEmbed(range.index, 'image', getImageUrl(url));
              quill.setSelection(range.index + 1);
            }
          }).catch(error => {
            alert('Erro ao fazer upload da imagem. Tente novamente.');
          });
        }
        break;
      }
    }
  };

  // Handler para arrastar e soltar imagens
  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files[0];
    if (file && file.type.startsWith('image/')) {
      uploadImage(file).then(url => {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', getImageUrl(url));
          quill.setSelection(range.index + 1);
        }
      }).catch(error => {
        alert('Erro ao fazer upload da imagem. Tente novamente.');
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    // Validação básica no frontend
    if (!formData.title || formData.title.trim().length < 3) {
      setMessage('O título deve ter no mínimo 3 caracteres');
      return;
    }

    if (!formData.content || formData.content.trim().length < 10) {
      // Remover tags HTML para validar conteúdo real
      const textContent = formData.content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      if (textContent.length < 10) {
        setMessage('O conteúdo deve ter no mínimo 10 caracteres');
        return;
      }
    }

    try {
      // Garantir que temos um CSRF token antes de fazer a requisição
      // Se não tiver, fazer uma requisição GET primeiro para obter
      const csrfToken = document.cookie.split(';').find(c => c.trim().startsWith('csrf_token='));
      if (!csrfToken) {
        // Obter CSRF token fazendo uma requisição GET
        await api.get('/auth/verify');
      }

      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('content', formData.content);
      formDataToSend.append('published', formData.published ? 'true' : 'false');
      if (image) {
        formDataToSend.append('image', image);
      }

      if (editingNews) {
        await api.put(`/news/${editingNews.id}`, formDataToSend);
        setMessage('Notícia atualizada com sucesso!');
      } else {
        await api.post('/news', formDataToSend);
        setMessage('Notícia criada com sucesso!');
      }

      resetForm();
      fetchNews();
    } catch (error) {
      log.error('Erro ao salvar notícia:', error);
      let errorMessage = 'Erro ao salvar notícia';
      
      // Tratar especificamente erros de CSRF
      if (error.response?.status === 403 && error.response?.data?.code === 'CSRF_TOKEN_INVALID') {
        errorMessage = 'Erro de autenticação. Por favor, faça login novamente.';
      }
      if (error.response?.status === 413) {
        errorMessage = 'Erro 413: imagem ou arquivo muito grande. O Nginx está limitando. No VPS adicione client_max_body_size 12M; no Nginx (bloco server e em location /api/) e recarregue. Veja NGINX_UPLOAD_FIX.md. Limite: 5MB.';
      } else if (error.response?.data) {
        if (error.response.data.details && Array.isArray(error.response.data.details)) {
          errorMessage = error.response.data.details.map(d => d.message || d.msg || d).join(', ');
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      }
      setMessage(errorMessage);
    }
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      published: newsItem.published
    });
    setImagePreview(newsItem.image_url ? getImageUrl(newsItem.image_url) : null);
    setImage(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta notícia?')) {
      return;
    }

    try {
      await api.delete(`/news/${id}`);
      setMessage('Notícia deletada com sucesso!');
      fetchNews();
    } catch (error) {
      setMessage('Erro ao deletar notícia');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', published: false });
    setBannerFormData({ title: '', link_url: '', active: true, display_order: 0 });
    setTopImageFormData({ title: '', link_url: '', active: true, display_order: 0 });
    setVideoFormData({ youtube_url: '', title: '', description: '', active: true, display_order: 0 });
    setImage(null);
    setImagePreview(null);
    setEditingNews(null);
    setEditingBanner(null);
    setEditingGuide(null);
    setEditingTopImage(null);
    setEditingVideo(null);
    setShowForm(false);
  };

  // Handlers para Banners
  const handleBannerInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBannerFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBannerSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!image && !editingBanner) {
      setMessage('Imagem é obrigatória');
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', bannerFormData.title.trim());
      formDataToSend.append('link_url', bannerFormData.link_url.trim());
      formDataToSend.append('active', bannerFormData.active ? 'true' : 'false');
      formDataToSend.append('display_order', bannerFormData.display_order || '0');
      if (image) {
        formDataToSend.append('image', image);
      }

      if (editingBanner) {
        await api.put(`/news/banners/${editingBanner.id}`, formDataToSend);
        setMessage('Banner atualizado com sucesso!');
      } else {
        await api.post('/news/banners', formDataToSend);
        setMessage('Banner criado com sucesso!');
      }

      resetForm();
      fetchBanners();
    } catch (error) {
      log.error('Erro ao salvar banner:', error);
      const errorMessage = error.response?.status === 413
        ? 'Erro 413: imagem muito grande. No VPS adicione client_max_body_size 12M; no Nginx e recarregue. Veja NGINX_UPLOAD_FIX.md. Limite: 10MB.'
        : (error.response?.data?.error || 'Erro ao salvar banner');
      setMessage(errorMessage);
    }
  };

  const handleBannerEdit = (banner) => {
    setEditingBanner(banner);
    setBannerFormData({
      title: banner.title || '',
      link_url: banner.link_url || '',
      active: banner.active,
      display_order: banner.display_order || 0
    });
    setImagePreview(banner.image_url ? getImageUrl(banner.image_url) : null);
    setImage(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBannerDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este banner?')) {
      return;
    }

    try {
      await api.delete(`/news/banners/${id}`);
      setMessage('Banner deletado com sucesso!');
      fetchBanners();
    } catch (error) {
      setMessage('Erro ao deletar banner');
    }
  };

  // Handlers para Guias
  const handleGuideSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    // Validação básica no frontend
    if (!formData.title || formData.title.trim().length < 3) {
      setMessage('O título deve ter no mínimo 3 caracteres');
      return;
    }

    if (!formData.content || formData.content.trim().length < 10) {
      const textContent = formData.content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      if (textContent.length < 10) {
        setMessage('O conteúdo deve ter no mínimo 10 caracteres');
        return;
      }
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('content', formData.content);
      formDataToSend.append('published', formData.published ? 'true' : 'false');
      if (image) {
        formDataToSend.append('image', image);
      }

      if (editingGuide) {
        await api.put(`/guides/${editingGuide.id}`, formDataToSend);
        setMessage('Guia atualizado com sucesso!');
      } else {
        await api.post('/guides', formDataToSend);
        setMessage('Guia criado com sucesso!');
      }

      resetForm();
      fetchGuides();
    } catch (error) {
      log.error('Erro ao salvar guia:', error);
      let errorMessage = 'Erro ao salvar guia';
      if (error.response?.status === 413) {
        errorMessage = 'Erro 413: imagem ou arquivo muito grande. O Nginx está limitando. No VPS adicione client_max_body_size 12M; no Nginx e recarregue. Veja NGINX_UPLOAD_FIX.md. Limite: 5MB.';
      } else if (error.response?.data) {
        if (error.response.data.details && Array.isArray(error.response.data.details)) {
          errorMessage = error.response.data.details.map(d => d.message || d.msg || d).join(', ');
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      }
      setMessage(errorMessage);
    }
  };

  const handleGuideEdit = (guideItem) => {
    setEditingGuide(guideItem);
    setFormData({
      title: guideItem.title,
      content: guideItem.content,
      published: guideItem.published
    });
    setImagePreview(guideItem.image_url ? getImageUrl(guideItem.image_url) : null);
    setImage(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGuideDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este guia?')) {
      return;
    }

    try {
      await api.delete(`/guides/${id}`);
      setMessage('Guia deletado com sucesso!');
      fetchGuides();
    } catch (error) {
      setMessage('Erro ao deletar guia');
    }
  };

  // Handlers para Imagens de Topo
  const handleTopImageInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTopImageFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTopImageSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!image && !editingTopImage) {
      setMessage('Imagem é obrigatória');
      return;
    }

    try {
      // Garantir que temos um CSRF token antes de fazer a requisição
      const csrfToken = document.cookie.split(';').find(c => c.trim().startsWith('csrf_token='));
      if (!csrfToken) {
        // Obter CSRF token fazendo uma requisição GET
        await api.get('/auth/verify');
      }

      const formDataToSend = new FormData();
      formDataToSend.append('title', topImageFormData.title || '');
      formDataToSend.append('link_url', topImageFormData.link_url || '');
      formDataToSend.append('active', topImageFormData.active ? 'true' : 'false');
      formDataToSend.append('display_order', topImageFormData.display_order || '0');
      if (image) {
        formDataToSend.append('image', image);
      }

      if (editingTopImage) {
        await api.put(`/news/top-images/${editingTopImage.id}`, formDataToSend);
        setMessage('Imagem de topo atualizada com sucesso!');
      } else {
        await api.post('/news/top-images', formDataToSend);
        setMessage('Imagem de topo criada com sucesso!');
      }

      resetForm();
      fetchTopImages();
    } catch (error) {
      log.error('Erro ao salvar imagem de topo:', error);
      let errorMessage = 'Erro ao salvar imagem de topo';
      
      // Tratar especificamente erros de CSRF
      if (error.response?.status === 403 && error.response?.data?.code === 'CSRF_TOKEN_INVALID') {
        errorMessage = 'Erro de autenticação. Por favor, faça login novamente.';
      } else if (error.response?.status === 413) {
        errorMessage = 'Erro 413: imagem muito grande. Limite: 5MB.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setMessage(errorMessage);
    }
  };

  const handleTopImageEdit = (topImage) => {
    setEditingTopImage(topImage);
    setTopImageFormData({
      title: topImage.title || '',
      link_url: topImage.link_url || '',
      active: topImage.active,
      display_order: topImage.display_order || 0
    });
    setImagePreview(topImage.image_url ? getImageUrl(topImage.image_url) : null);
    setImage(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTopImageDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta imagem de topo?')) {
      return;
    }

    try {
      await api.delete(`/news/top-images/${id}`);
      setMessage('Imagem de topo deletada com sucesso!');
      fetchTopImages();
    } catch (error) {
      setMessage('Erro ao deletar imagem de topo');
    }
  };

  // Funções para gerenciar vídeos do YouTube
  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVideo) {
        await api.put(`/news/youtube-videos/${editingVideo.id}`, videoFormData);
        setMessage('Vídeo atualizado com sucesso!');
      } else {
        await api.post('/news/youtube-videos', videoFormData);
        setMessage('Vídeo criado com sucesso!');
      }
      setShowForm(false);
      setEditingVideo(null);
      setVideoFormData({
        youtube_url: '',
        title: '',
        description: '',
        active: true,
        display_order: 0
      });
      fetchYoutubeVideos();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Erro ao salvar vídeo');
    }
  };

  const handleVideoEdit = (video) => {
    setEditingVideo(video);
    setVideoFormData({
      youtube_url: video.youtube_url || '',
      title: video.title || '',
      description: video.description || '',
      active: video.active !== undefined ? video.active : true,
      display_order: video.display_order || 0
    });
    setShowForm(true);
  };

  const handleVideoDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este vídeo?')) {
      return;
    }

    try {
      await api.delete(`/news/youtube-videos/${id}`);
      setMessage('Vídeo deletado com sucesso!');
      fetchYoutubeVideos();
    } catch (error) {
      setMessage('Erro ao deletar vídeo');
    }
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="admin-news-page">
      <div className="container">
        <div className="admin-header">
          <h1>GERENCIAR CONTEÚDO</h1>
        </div>

        {/* Tabs */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'news' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('news');
              resetForm();
            }}
          >
            Notícias
          </button>
          <button
            className={`admin-tab ${activeTab === 'banners' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('banners');
              resetForm();
            }}
          >
            Banners
          </button>
          <button
            className={`admin-tab ${activeTab === 'guides' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('guides');
              resetForm();
            }}
          >
            Guias
          </button>
          <button
            className={`admin-tab ${activeTab === 'topImages' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('topImages');
              resetForm();
            }}
          >
            Imagens de Topo
          </button>
          <button
            className={`admin-tab ${activeTab === 'youtubeVideos' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('youtubeVideos');
              resetForm();
            }}
          >
            Vídeos YouTube
          </button>
        </div>

        <div className="admin-header-actions">
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(!showForm);
            }}
          >
            {showForm ? 'Cancelar' : activeTab === 'news' ? '+ Nova Notícia' : activeTab === 'banners' ? '+ Novo Banner' : activeTab === 'guides' ? '+ Novo Guia' : '+ Nova Imagem de Topo'}
          </button>
        </div>

        {message && (
          <div className={message.includes('Erro') ? 'error' : 'success'}>
            {message}
          </div>
        )}

        {activeTab === 'news' && (
          <>
            {showForm && (
              <div className="news-form-card">
                <h2>{editingNews ? 'Editar Notícia' : 'Nova Notícia'}</h2>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>Título</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      maxLength={200}
                    />
                  </div>

                  <div className="form-group">
                    <label>Imagem</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {imagePreview && (
                      <img src={imagePreview} alt="Preview" className="image-preview" />
                    )}
                  </div>

                  <div className="form-group">
                    <label>Conteúdo</label>
                    <div 
                      onPaste={handleImagePaste}
                      onDrop={handleImageDrop}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <div 
                        onKeyDown={(e) => {
                          // Interceptar erros de addRange antes que o Quill os processe
                          try {
                            // Permitir que o evento continue normalmente
                          } catch (error) {
                            if (error.message && error.message.includes('addRange')) {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }
                        }}
                      >
                        <ReactQuill
                          ref={(el) => {
                            quillRef.current = el;
                            if (el) {
                              try {
                                const editor = el.getEditor();
                                quillEditorRef.current = editor;
                                
                                // Sobrescrever métodos que podem causar erro addRange
                                const originalSetSelection = editor.setSelection.bind(editor);
                                editor.setSelection = function(range, source) {
                                  try {
                                    if (range && typeof range === 'object' && range.index !== undefined) {
                                      const length = this.getLength();
                                      if (range.index < 0 || range.index >= length) {
                                        // Ajustar range para ser válido
                                        range.index = Math.max(0, Math.min(range.index, length - 1));
                                      }
                                    }
                                    return originalSetSelection(range, source);
                                  } catch (error) {
                                    if (error.message && error.message.includes('addRange')) {
                                      // Ignorar erro de addRange silenciosamente
                                      return;
                                    }
                                    throw error;
                                  }
                                };
                                
                                // Interceptar getSelection também
                                const originalGetSelection = editor.getSelection.bind(editor);
                                editor.getSelection = function(focus) {
                                  try {
                                    return originalGetSelection(focus);
                                  } catch (error) {
                                    if (error.message && error.message.includes('addRange')) {
                                      // Retornar null se houver erro de range
                                      return null;
                                    }
                                    throw error;
                                  }
                                };
                              } catch (error) {
                                // Ignorar erros ao obter editor
                                log.warn('Aviso ao obter editor Quill:', error.message);
                              }
                            }
                          }}
                          theme="snow"
                          value={formData.content || ''}
                          onChange={handleContentChange}
                          modules={quillModules}
                          placeholder="Escreva o conteúdo da notícia aqui... (Você pode arrastar imagens ou colar com Ctrl+V)"
                          onFocus={() => {
                            // Não fazer nada no onFocus para evitar conflitos
                          }}
                          bounds="self"
                          preserveWhitespace
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="published"
                        checked={formData.published}
                        onChange={handleInputChange}
                      />
                      Publicar imediatamente
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      {editingNews ? 'Atualizar' : 'Criar'} Notícia
                    </button>
                    <button type="button" className="btn btn-danger" onClick={resetForm}>
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="news-list">
              <h2>Notícias Existentes</h2>
              {news.length > 0 ? (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Título</th>
                      <th>Status</th>
                      <th>Data</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {news.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.title}</td>
                        <td>
                          <span className={`status ${item.published ? 'published' : 'draft'}`}>
                            {item.published ? 'Publicada' : 'Rascunho'}
                          </span>
                        </td>
                        <td>{new Date(item.created_at).toLocaleDateString('pt-BR')}</td>
                        <td>
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(item)}
                          >
                            Editar
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(item.id)}
                          >
                            Deletar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>Nenhuma notícia criada ainda.</p>
              )}
            </div>
          </>
        )}

        {activeTab === 'banners' && (
          <>
            {showForm && (
              <div className="news-form-card">
                <h2>{editingBanner ? 'Editar Banner' : 'Novo Banner'}</h2>
                <form onSubmit={handleBannerSubmit}>
                  <div className="form-group">
                    <label>Imagem *</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      required={!editingBanner}
                    />
                    {imagePreview && (
                      <img src={imagePreview} alt="Preview" className="image-preview" />
                    )}
                  </div>

                  <div className="form-group">
                    <label>Título (opcional)</label>
                    <input
                      type="text"
                      name="title"
                      value={bannerFormData.title}
                      onChange={handleBannerInputChange}
                      maxLength={200}
                      placeholder="Título que aparecerá no overlay do banner"
                    />
                  </div>

                  <div className="form-group">
                    <label>Link URL (opcional)</label>
                    <input
                      type="url"
                      name="link_url"
                      value={bannerFormData.link_url}
                      onChange={handleBannerInputChange}
                      placeholder="https://exemplo.com (onde o banner levará ao clicar)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Ordem de Exibição</label>
                    <input
                      type="number"
                      name="display_order"
                      value={bannerFormData.display_order}
                      onChange={handleBannerInputChange}
                      min="0"
                      placeholder="0"
                    />
                    <small>Banners com menor número aparecem primeiro</small>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="active"
                        checked={bannerFormData.active}
                        onChange={handleBannerInputChange}
                      />
                      Ativo (exibir no carrossel)
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      {editingBanner ? 'Atualizar' : 'Criar'} Banner
                    </button>
                    <button type="button" className="btn btn-danger" onClick={resetForm}>
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="news-list">
              <h2>Banners Existentes</h2>
              {banners.length > 0 ? (
                <div className="banners-grid">
                  {banners.map((banner) => (
                    <div key={banner.id} className="banner-card">
                      <img 
                        src={getImageUrl(banner.image_url)} 
                        alt={banner.title || 'Banner'}
                        className="banner-card-image"
                      />
                      <div className="banner-card-info">
                        <h4>{banner.title || 'Sem título'}</h4>
                        <p className="banner-status">
                          <span className={`status ${banner.active ? 'published' : 'draft'}`}>
                            {banner.active ? 'Ativo' : 'Inativo'}
                          </span>
                          <span className="banner-order">Ordem: {banner.display_order}</span>
                        </p>
                        {banner.link_url && (
                          <p className="banner-link">Link: {banner.link_url}</p>
                        )}
                        <div className="banner-actions">
                          <button
                            className="btn-edit"
                            onClick={() => handleBannerEdit(banner)}
                          >
                            Editar
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleBannerDelete(banner.id)}
                          >
                            Deletar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>Nenhum banner criado ainda.</p>
              )}
            </div>
          </>
        )}

        {activeTab === 'guides' && (
          <>
            {showForm && (
              <div className="news-form-card">
                <h2>{editingGuide ? 'Editar Guia' : 'Novo Guia'}</h2>
                <form onSubmit={handleGuideSubmit}>
                  <div className="form-group">
                    <label>Título</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      maxLength={200}
                    />
                  </div>

                  <div className="form-group">
                    <label>Imagem</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {imagePreview && (
                      <img src={imagePreview} alt="Preview" className="image-preview" />
                    )}
                    {editingGuide && editingGuide.image_url && !image && (
                      <div>
                        <small>Imagem atual:</small>
                        <img 
                          src={getImageUrl(editingGuide.image_url)} 
                          alt="Atual" 
                          className="image-preview"
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Conteúdo</label>
                    <div 
                      onPaste={handleImagePaste}
                      onDrop={handleImageDrop}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      <ReactQuill
                        ref={(el) => {
                          quillRef.current = el;
                          if (el) {
                            quillEditorRef.current = el.getEditor();
                          }
                        }}
                        theme="snow"
                        value={formData.content}
                        onChange={handleContentChange}
                        modules={quillModules}
                        placeholder="Escreva o conteúdo do guia aqui... (Você pode arrastar imagens ou colar com Ctrl+V)"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="published"
                        checked={formData.published}
                        onChange={handleInputChange}
                      />
                      Publicar imediatamente
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      {editingGuide ? 'Atualizar' : 'Criar'} Guia
                    </button>
                    <button type="button" className="btn btn-danger" onClick={resetForm}>
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="news-list">
              <h2>Guias Existentes</h2>
              {guides.length > 0 ? (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Título</th>
                      <th>Status</th>
                      <th>Data</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guides.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.title}</td>
                        <td>
                          <span className={`status ${item.published ? 'published' : 'draft'}`}>
                            {item.published ? 'Publicado' : 'Rascunho'}
                          </span>
                        </td>
                        <td>{new Date(item.created_at).toLocaleDateString('pt-BR')}</td>
                        <td>
                          <button
                            className="btn-edit"
                            onClick={() => handleGuideEdit(item)}
                          >
                            Editar
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleGuideDelete(item.id)}
                          >
                            Deletar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>Nenhum guia criado ainda.</p>
              )}
            </div>
          </>
        )}

        {activeTab === 'topImages' && (
          <>
            {showForm && (
              <div className="news-form-card">
                <h2>{editingTopImage ? 'Editar Imagem de Topo' : 'Nova Imagem de Topo'}</h2>
                <form onSubmit={handleTopImageSubmit}>
                  <div className="form-group">
                    <label>Imagem *</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      required={!editingTopImage}
                    />
                    {imagePreview && (
                      <img src={imagePreview} alt="Preview" className="image-preview" />
                    )}
                    {editingTopImage && editingTopImage.image_url && !image && (
                      <div>
                        <small>Imagem atual:</small>
                        <img 
                          src={getImageUrl(editingTopImage.image_url)} 
                          alt="Atual" 
                          className="image-preview"
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Título (opcional)</label>
                    <input
                      type="text"
                      name="title"
                      value={topImageFormData.title}
                      onChange={handleTopImageInputChange}
                      placeholder="Título da imagem"
                      maxLength={200}
                    />
                  </div>

                  <div className="form-group">
                    <label>Link URL (opcional)</label>
                    <input
                      type="url"
                      name="link_url"
                      value={topImageFormData.link_url}
                      onChange={handleTopImageInputChange}
                      placeholder="https://exemplo.com (onde a imagem levará ao clicar)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Ordem de Exibição</label>
                    <input
                      type="number"
                      name="display_order"
                      value={topImageFormData.display_order}
                      onChange={handleTopImageInputChange}
                      min="0"
                    />
                    <small>Imagens com menor número aparecem primeiro</small>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="active"
                        checked={topImageFormData.active}
                        onChange={handleTopImageInputChange}
                      />
                      Ativo (exibir no carrossel)
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                      {editingTopImage ? 'Atualizar' : 'Criar'} Imagem de Topo
                    </button>
                    <button type="button" className="btn btn-danger" onClick={resetForm}>
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="news-list">
              <h2>Imagens de Topo Existentes</h2>
              {topImages.length > 0 ? (
                <div className="banner-grid">
                  {topImages.map((item) => (
                    <div key={item.id} className="banner-card">
                      <img 
                        src={getImageUrl(item.image_url)} 
                        alt={item.title || 'Imagem de topo'} 
                        className="banner-preview"
                      />
                      <div className="banner-info">
                        <h4>{item.title || 'Sem título'}</h4>
                        <p>Ordem: {item.display_order}</p>
                        <p>Status: {item.active ? 'Ativo' : 'Inativo'}</p>
                        <div className="banner-actions">
                          <button
                            className="btn-edit"
                            onClick={() => handleTopImageEdit(item)}
                          >
                            Editar
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleTopImageDelete(item.id)}
                          >
                            Deletar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>Nenhuma imagem de topo criada ainda.</p>
              )}
            </div>
          </>
        )}

        {/* Aba de Vídeos do YouTube */}
        {activeTab === 'youtubeVideos' && (
          <>
            {showForm && (
              <div className="news-form-card">
                <h2>{editingVideo ? 'Editar Vídeo' : 'Adicionar Novo Vídeo'}</h2>
                <form onSubmit={handleVideoSubmit}>
                  <div className="form-group">
                    <label>URL do YouTube *</label>
                    <input
                      type="text"
                      name="youtube_url"
                      value={videoFormData.youtube_url}
                      onChange={(e) => setVideoFormData({ ...videoFormData, youtube_url: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=VIDEO_ID ou https://youtu.be/VIDEO_ID"
                      required
                    />
                    <small>Cole o link completo do vídeo do YouTube</small>
                  </div>

                  <div className="form-group">
                    <label>Título</label>
                    <input
                      type="text"
                      name="title"
                      value={videoFormData.title}
                      onChange={(e) => setVideoFormData({ ...videoFormData, title: e.target.value })}
                      placeholder="Título do vídeo (opcional)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Descrição</label>
                    <textarea
                      name="description"
                      value={videoFormData.description}
                      onChange={(e) => setVideoFormData({ ...videoFormData, description: e.target.value })}
                      placeholder="Descrição do vídeo (opcional)"
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label>Ordem de Exibição</label>
                    <input
                      type="number"
                      name="display_order"
                      value={videoFormData.display_order}
                      onChange={(e) => setVideoFormData({ ...videoFormData, display_order: parseInt(e.target.value) || 0 })}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="active"
                        checked={videoFormData.active}
                        onChange={(e) => setVideoFormData({ ...videoFormData, active: e.target.checked })}
                      />
                      {' '}Ativo
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn-primary">
                      {editingVideo ? 'Atualizar' : 'Criar'} Vídeo
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setShowForm(false);
                        setEditingVideo(null);
                        setVideoFormData({
                          youtube_url: '',
                          title: '',
                          description: '',
                          active: true,
                          display_order: 0
                        });
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="news-list">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Vídeos do YouTube</h2>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setShowForm(true);
                    setEditingVideo(null);
                    setVideoFormData({
                      youtube_url: '',
                      title: '',
                      description: '',
                      active: true,
                      display_order: 0
                    });
                  }}
                >
                  + Adicionar Vídeo
                </button>
              </div>

              {youtubeVideos.length > 0 ? (
                <div className="banner-grid">
                  {youtubeVideos.map((video) => {
                    // Extrair ID do vídeo
                    const extractVideoId = (url) => {
                      if (!url) return null;
                      const patterns = [
                        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
                        /youtube\.com\/watch\?.*v=([^&\n?#]+)/
                      ];
                      for (const pattern of patterns) {
                        const match = url.match(pattern);
                        if (match && match[1]) return match[1];
                      }
                      return null;
                    };

                    const videoId = extractVideoId(video.youtube_url);
                    if (!videoId) return null;

                    return (
                      <div key={video.id} className="banner-card">
                        <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000', borderRadius: '8px', overflow: 'hidden', marginBottom: '10px' }}>
                          <img
                            src={video.thumbnail_url || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                            alt={video.title || 'Vídeo do YouTube'}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                            }}
                          />
                        </div>
                        <div className="banner-info">
                          <h4>{video.title || 'Sem título'}</h4>
                          {video.description && (
                            <p style={{ fontSize: '12px', color: '#666', margin: '5px 0' }}>
                              {video.description.length > 100 ? video.description.substring(0, 100) + '...' : video.description}
                            </p>
                          )}
                          <p>Ordem: {video.display_order}</p>
                          <p>Status: {video.active ? 'Ativo' : 'Inativo'}</p>
                          <div className="banner-actions">
                            <button
                              className="btn-edit"
                              onClick={() => handleVideoEdit(video)}
                            >
                              Editar
                            </button>
                            <button
                              className="btn-delete"
                              onClick={() => handleVideoDelete(video.id)}
                            >
                              Deletar
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>Nenhum vídeo criado ainda.</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminNews;

